// Dashboard: the QuoteFlow section, for companies with QuoteFlow (or the trial
// switch) only. The last 30 days of quotes (sent, opened, accepted, value won,
// acceptance rate), what needs the installer now (opened but unanswered, drafts
// not sent, sent but not opened for 3+ days, quote terms still to accept) and the
// latest quotes. Built from the quotes list (100-api quotes GET /v1/quotes).
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, FilePlus2, FileText, Send } from "lucide-react";
import { QuoteStatusChip } from "../pages/quotes/QuotesPage";
import { useQuotesApi, type QuoteAccess, type QuoteSummary } from "../quotes/api";
import { gbp, pct, when } from "../quotes/format";

const DAY = 24 * 3600 * 1000;
// The Value accepted card: including or excluding VAT (remembered on this browser)
const VAT_KEY = "xq-dash-value-inc-vat";
const within = (iso: string | null | undefined, days: number) => !!iso && Date.now() - new Date(iso).getTime() <= days * DAY;

export function QuoteFlowPanel() {
  const api = useQuotesApi();
  const navigate = useNavigate();
  const [access, setAccess] = useState<QuoteAccess | null>(null);
  const [quotes, setQuotes] = useState<QuoteSummary[] | null>(null);
  const [incVat, setIncVat] = useState<boolean>(() => {
    try {
      return localStorage.getItem(VAT_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const toggleVat = (v: boolean) => {
    setIncVat(v);
    try {
      localStorage.setItem(VAT_KEY, v ? "1" : "0");
    } catch {
      // the choice just won't be remembered
    }
  };

  // Only companies with QuoteFlow see the section (a failed check shows nothing)
  useEffect(() => {
    api
      .getAccess()
      .then((a) => {
        setAccess(a);
        if (a.entitled) api.listQuotes().then(setQuotes).catch(() => setQuotes([]));
      })
      .catch(() => undefined);
  }, [api]);

  const figures = useMemo(() => {
    const q = quotes ?? [];
    // Sent in the period: first sent within 30 days; answered: last change within 30 days
    const sent = q.filter((x) => within(x.publish?.firstSentAt ?? x.publish?.sentAt, 30));
    const opened = sent.filter((x) => (x.publish?.views ?? 0) > 0);
    const accepted = q.filter((x) => x.status === "accepted" && within(x.updatedAt, 30));
    const declined = q.filter((x) => x.status === "declined" && within(x.updatedAt, 30));
    const won = accepted.reduce((sum, x) => sum + (x.acceptance?.totals.gross ?? 0), 0);
    const wonNet = accepted.reduce((sum, x) => sum + (x.acceptance?.totals.net ?? 0), 0);
    const answered = accepted.length + declined.length;
    return {
      sent: sent.length,
      opened: opened.length,
      accepted: accepted.length,
      won,
      wonNet,
      rate: answered ? (accepted.length / answered) * 100 : null,
      // Needs the installer now
      openedWaiting: q.filter((x) => x.status === "viewed"),
      drafts: q.filter((x) => x.status === "draft"),
      unopened: q.filter((x) => x.status === "sent" && !x.publish?.views && !within(x.publish?.sentAt, 3)),
      latest: [...q].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")).slice(0, 5),
    };
  }, [quotes]);

  if (!access?.entitled) return null;

  const todo: { key: string; text: string; to: string; icon: ReactNode }[] = [];
  if (!access.termsAccepted) todo.push({ key: "terms", text: "Accept the quote terms before you send quotes", to: "/quotes/settings", icon: <FileText size={15} aria-hidden="true" /> });
  if (figures.openedWaiting.length)
    todo.push({ key: "viewed", text: `${figures.openedWaiting.length} opened by the customer, not answered yet: a good time to call`, to: "/quotes", icon: <Eye size={15} aria-hidden="true" /> });
  if (figures.unopened.length) todo.push({ key: "unopened", text: `${figures.unopened.length} sent 3+ days ago and not opened yet`, to: "/quotes", icon: <Send size={15} aria-hidden="true" /> });
  if (figures.drafts.length) todo.push({ key: "drafts", text: `${figures.drafts.length} draft${figures.drafts.length === 1 ? "" : "s"} not sent yet`, to: "/quotes", icon: <FileText size={15} aria-hidden="true" /> });

  return (
    <section className="dash-quoteflow" aria-label="QuoteFlow">
      <div className="dash-quoteflow-head">
        <h2>QuoteFlow</h2>
        <Link to="/quotes/new" className="btn btn-primary">
          <FilePlus2 size={16} aria-hidden="true" /> New quote
        </Link>
      </div>

      <div className="kpi-row">
        <div className="card kpi">
          <div className="kpi-label">Quotes sent</div>
          <div className="kpi-value">{quotes ? figures.sent : "—"}</div>
          <div className="kpi-note">Last 30 days</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Opened by the customer</div>
          <div className="kpi-value">{quotes ? figures.opened : "—"}</div>
          <div className="kpi-note">{figures.sent ? `${pct((figures.opened / figures.sent) * 100)} of those sent` : "Last 30 days"}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Accepted</div>
          <div className="kpi-value">{quotes ? figures.accepted : "—"}</div>
          <div className="kpi-note">{figures.rate === null ? "No answers yet" : `${pct(figures.rate)} of answered quotes`}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label kpi-label-row">
            Value accepted
            <label className="kpi-switch" title="Show the value including or excluding VAT">
              <input type="checkbox" role="switch" checked={incVat} onChange={(e) => toggleVat(e.target.checked)} aria-label="Include VAT" />
              <span className="kpi-switch-track" aria-hidden="true" />
              VAT
            </label>
          </div>
          <div className="kpi-value">{quotes ? gbp(incVat ? figures.won : figures.wonNet) : "—"}</div>
          <div className="kpi-note">{incVat ? "Including VAT" : "Excluding VAT"}, before grants</div>
        </div>
      </div>

      <div className="dash-quoteflow-grid">
        <div className="card">
          <h3>Needs you</h3>
          {quotes === null ? (
            <p className="muted">Loading…</p>
          ) : todo.length === 0 ? (
            <p className="muted">Nothing waiting. Every quote is either answered or recently sent.</p>
          ) : (
            <ul className="dash-todo">
              {todo.map((t) => (
                <li key={t.key}>
                  <Link to={t.to}>
                    {t.icon} {t.text}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card">
          <div className="dash-quoteflow-head">
            <h3>Latest quotes</h3>
            <Link to="/quotes" className="link-button small">
              All quotes
            </Link>
          </div>
          {quotes && quotes.length === 0 ? (
            <p className="muted">
              No quotes yet. <Link to="/quotes/new">Build your first</Link> from a postcode, or switch on detailed quotes on your ASHP Estimators.
            </p>
          ) : (
            <table className="dash-quotes">
              <tbody>
                {figures.latest.map((x) => (
                  <tr key={x.quoteId} onClick={() => navigate(`/quotes/${x.quoteId}`)}>
                    <td>
                      <Link to={`/quotes/${x.quoteId}`} onClick={(e) => e.stopPropagation()}>
                        {x.reference}
                      </Link>
                    </td>
                    <td>{x.customer?.name || <span className="muted">No name</span>}</td>
                    <td>
                      <QuoteStatusChip status={x.status} />
                    </td>
                    <td className="num">{x.status === "accepted" && x.acceptance ? gbp(x.acceptance.totals.payable) : x.summary?.payable != null ? gbp(x.summary.payable) : "—"}</td>
                    <td className="muted">{when(x.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}
